import { formatApiErrorDetail } from "@/lib/api";
// Field Radar · TenantBridge — tenant magic-link view.
//
// Zero-friction: tenant taps the SMS/email link → lands here → sees the
// full thread + replies. No signup, no PIN. Magic token is bearer-auth.
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneTilt, Shield, CheckCircle, Calendar, DownloadSimple, XCircle, Camera, Image as ImageIcon, X, Paperclip, Sparkle, Wrench, ThumbsUp } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

function EntryNoticeCard({ notice, token, onReload }) {
  const [pickedIdx, setPickedIdx] = useState(0);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [declineMode, setDeclineMode] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [lockboxOnly, setLockboxOnly] = useState(false);

  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-US", {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch { return iso; }
  };

  const sign = async () => {
    if (!name.trim() || name.trim().length < 2) { toast.error("Type your full legal name"); return; }
    setBusy(true);
    try {
      await axios.post(
        `${BASE}/api/public/tb/${token}/entry-notices/${notice.id}/sign`,
        { window_index: pickedIdx, signed_name: name.trim(), lockbox_only: lockboxOnly });
      toast.success(lockboxOnly ? "Signed — lockbox authorized" : "Entry authorization signed");
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't sign")); }
    finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${BASE}/api/public/tb/${token}/entry-notices/${notice.id}/decline`,
        { reason: declineReason.trim() });
      toast.success("Declined — property manager will follow up");
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't decline")); }
    finally { setBusy(false); }
  };

  if (notice.status === "signed") {
    const cw = notice.chosen_window || {};
    return (
      <div className="max-w-md mx-auto w-full p-3 bg-emerald-50 border-b-2 border-emerald-500" data-testid={`tenant-notice-signed-${notice.id}`}>
        <div className="flex items-center gap-2 text-emerald-800">
          <CheckCircle weight="fill" size={20}/>
          <div className="font-black uppercase tracking-widest text-xs">Entry Authorized</div>
        </div>
        <div className="mt-2 text-sm text-emerald-900">
          <div>{notice.reason}</div>
          <div className="mt-1 font-mono text-[11px]">{fmt(cw.start)} → {fmt(cw.end)}</div>
          <div className="mt-0.5 text-[10px] opacity-70">Signed as {notice.signed_name}</div>
          {notice.lockbox_only && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-cyan-100 border border-cyan-500 text-cyan-800 text-[10px] font-black uppercase tracking-widest"
              data-testid={`tenant-notice-lockbox-badge-${notice.id}`}>
              🔐 Will not be home · lockbox authorized
            </div>
          )}
        </div>
        <a href={`${BASE}/api/public/tb/${token}/entry-notices/${notice.id}/pdf`}
          target="_blank" rel="noreferrer" data-testid={`tenant-notice-pdf-${notice.id}`}
          className="mt-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-700 hover:underline">
          <DownloadSimple size={12}/> Download PDF
        </a>
      </div>
    );
  }

  if (notice.status === "declined" || notice.status === "canceled") {
    return (
      <div className="max-w-md mx-auto w-full p-3 bg-slate-100 border-b border-slate-200" data-testid={`tenant-notice-closed-${notice.id}`}>
        <div className="flex items-center gap-2 text-slate-500">
          <XCircle size={16}/>
          <div className="text-[11px] font-black uppercase tracking-widest">
            Entry request {notice.status}
          </div>
        </div>
        <div className="mt-1 text-xs italic text-slate-500">{notice.reason}</div>
      </div>
    );
  }

  // pending
  return (
    <div className="max-w-md mx-auto w-full p-3 bg-amber-50 border-b-2 border-amber-500" data-testid={`tenant-notice-pending-${notice.id}`}>
      <div className="flex items-center gap-2 text-amber-800">
        <Calendar weight="fill" size={20}/>
        <div className="font-black uppercase tracking-widest text-xs">Entry Request · Pick a Window</div>
      </div>
      <div className="mt-2 text-sm text-amber-900">{notice.reason}</div>
      {notice.tech_name && (
        <div className="mt-1 text-[11px] text-amber-700">Personnel: {notice.tech_name}</div>
      )}

      {!declineMode ? (
        <>
          <div className="mt-3 space-y-1.5">
            {(notice.windows || []).map((w, i) => (
              <label key={i} data-testid={`tenant-notice-window-${i}`}
                className={`flex items-center gap-2 p-2 border-2 cursor-pointer ${
                  pickedIdx === i ? "bg-white border-[#0f172a]" : "bg-white/50 border-amber-300 hover:border-amber-500"
                }`}>
                <input type="radio" checked={pickedIdx === i} onChange={() => setPickedIdx(i)}/>
                <span className="text-xs font-mono">{fmt(w.start)} → {fmt(w.end)}</span>
              </label>
            ))}
          </div>
          <div className="mt-3">
            <label className="text-[10px] font-black uppercase tracking-widest text-amber-800">Type your full legal name to sign</label>
            <input value={name} onChange={e => setName(e.target.value)}
              data-testid="tenant-notice-sign-name" placeholder="Sarah Johnson" maxLength={80}
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            <div className="mt-1 text-[9px] text-amber-700 italic">
              Legally binding under the federal E-SIGN Act + PA UETA. Timestamp + IP recorded.
            </div>
          </div>

          {/* Unattended-access consent — tenant declares they'll be gone and
              the tech can use the lockbox / entry code without waiting. */}
          <label data-testid="tenant-notice-lockbox-opt"
            className={`mt-3 flex items-start gap-2 p-2.5 border-2 cursor-pointer transition-colors ${
              lockboxOnly ? "bg-cyan-50 border-cyan-500" : "bg-white border-amber-300 hover:border-amber-500"
            }`}>
            <input type="checkbox" checked={lockboxOnly} onChange={e => setLockboxOnly(e.target.checked)}
              data-testid="tenant-notice-lockbox-checkbox"
              className="mt-0.5 h-4 w-4 accent-cyan-600 shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className={`text-xs font-black uppercase tracking-widest ${lockboxOnly ? "text-cyan-800" : "text-slate-700"}`}>
                I won't be home
              </div>
              <div className={`text-[11px] mt-0.5 ${lockboxOnly ? "text-cyan-700" : "text-slate-500"}`}>
                Free to use the lockbox / entry code during the window above.
              </div>
            </div>
          </label>

          <div className="mt-3 flex gap-2">
            <button onClick={sign} disabled={busy || !name.trim()} data-testid="tenant-notice-sign-btn"
              className="flex-1 h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40 inline-flex items-center justify-center gap-1">
              <CheckCircle weight="fill" size={14}/> {busy ? "Signing…" : "Sign & Authorize"}
            </button>
            <button onClick={() => setDeclineMode(true)} data-testid="tenant-notice-decline-btn"
              className="h-11 px-3 bg-white border-2 border-slate-300 text-slate-500 font-black uppercase text-[10px] tracking-widest hover:bg-slate-50">
              None work
            </button>
          </div>
        </>
      ) : (
        <div className="mt-3">
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-700">Tell them why (optional)</label>
          <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)}
            rows={2} data-testid="tenant-notice-decline-reason"
            placeholder="I'm out of town until…"
            className="mt-1 w-full px-3 py-2 border-2 border-[#0f172a] text-sm resize-none"/>
          <div className="mt-2 flex gap-2">
            <button onClick={decline} disabled={busy} data-testid="tenant-notice-decline-submit"
              className="flex-1 h-10 bg-slate-700 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
              {busy ? "Sending…" : "Send"}
            </button>
            <button onClick={() => setDeclineMode(false)} className="h-10 px-3 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest">
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TenantBridgeView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  // Pending photos the tenant has picked/snapped but not sent yet. Each
  // entry is { id, dataUrl, sizeMB, name } — dataUrl is what gets POSTed.
  const [pending, setPending] = useState([]);
  // AI self-diagnostic — tenant taps a card, Claude drafts 3–6 safe DIY
  // steps (with a curated static fallback) so we skip the truck roll if
  // it's a reset button. Card auto-shows on load when the tenant hasn't
  // replied yet, and stays available via a button after.
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagText, setDiagText] = useState("");
  const [diagVideo, setDiagVideo] = useState("");   // YouTube search URL
  const [diagThumb, setDiagThumb] = useState("");   // hqdefault thumbnail URL
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagDismissed, setDiagDismissed] = useState(false);
  // Language for AI diagnostic + fallback text. Auto-detect Spanish from
  // browser Accept-Language; tenant can flip via the globe toggle on the
  // card. Applies to prompt, footer, video query, generic fallback.
  const detectLang = () => {
    try {
      const b = (navigator?.language || navigator?.languages?.[0] || "").toLowerCase();
      return b.startsWith("es") ? "es" : "en";
    } catch { return "en"; }
  };
  const [diagLang, setDiagLang] = useState(detectLang());
  // Post-dispatch photo nudge — after "Still Broken", we prompt the tenant
  // to snap what they just tried BEFORE they type. Tech arrives prepared.
  const [showPhotoNudge, setShowPhotoNudge] = useState(false);
  const scrollRef = useRef(null);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const MAX_PHOTOS = 8;
  const MAX_MB = 10;   // per photo (raw). Backend caps decoded at 12MB.

  const load = async () => {
    try {
      const r = await axios.get(`${BASE}/api/public/tb/${token}`);
      setData(r.data);
    } catch { toast.error("Link invalid or expired"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data]);

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const slots = MAX_PHOTOS - pending.length;
    if (slots <= 0) { toast.error(`Max ${MAX_PHOTOS} photos per message`); return; }
    const picked = files.slice(0, slots);
    if (files.length > slots) toast.warning(`Only added ${slots} — max is ${MAX_PHOTOS}`);
    const additions = [];
    for (const f of picked) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name}: not an image`); continue; }
      const sizeMB = f.size / (1024 * 1024);
      if (sizeMB > MAX_MB) { toast.error(`${f.name}: over ${MAX_MB}MB`); continue; }
      try {
        const dataUrl = await fileToDataUrl(f);
        additions.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          dataUrl,
          sizeMB: Number(sizeMB.toFixed(1)),
          name: f.name || "photo.jpg",
        });
      } catch {
        toast.error(`${f.name}: couldn't read`);
      }
    }
    if (additions.length) {
      setPending((p) => [...p, ...additions]);
      // Photo landed → nudge card served its purpose; auto-collapse it so
      // the tenant sees the pending-strip and composer without competition.
      if (showPhotoNudge) setShowPhotoNudge(false);
    }
  };

  const removePending = (id) => setPending((p) => p.filter((x) => x.id !== id));

  const send = async () => {
    if (!reply.trim() && pending.length === 0) return;
    setBusy(true);
    try {
      const payload = { text: reply };
      if (pending.length) payload.photos = pending.map((p) => p.dataUrl);
      const res = await axios.post(`${BASE}/api/public/tb/${token}/messages`, payload);
      const failed = res?.data?.failed_photos || [];
      if (failed.length) toast.warning(`${failed.length} photo(s) couldn't be sent`);
      setReply("");
      setPending([]);
      setShowPhotoNudge(false);   // send fired → clear nudge too
      if (galleryInputRef.current) galleryInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't send")); }
    finally { setBusy(false); }
  };

  const loadDiagnostic = async (langOverride) => {
    if (diagBusy) return;
    const useLang = langOverride || diagLang;
    setDiagBusy(true);
    setDiagOpen(true);
    try {
      const r = await axios.post(`${BASE}/api/public/tb/${token}/self-diagnose`, null, {
        params: { lang: useLang },
      });
      setDiagText(r.data?.steps_markdown || "");
      setDiagVideo(r.data?.video_url || "");
      setDiagThumb(r.data?.video_thumb || "");
      if (langOverride) setDiagLang(langOverride);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't load quick fixes"));
      setDiagOpen(false);
    } finally { setDiagBusy(false); }
  };

  const markDiagnosticResolved = async () => {
    if (!confirm("Great! We'll close this request. If it comes back, just start a new one.\n\nMark as fixed?")) return;
    try {
      await axios.post(`${BASE}/api/public/tb/${token}/self-diagnose-resolved`);
      toast.success("Thanks — request closed. Have a great day!");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't close"));
    }
  };

  // Auto-decide whether to surface the "try quick fixes" card:
  //   - open threads only
  //   - tenant hasn't sent a message yet (i.e. they just got the link)
  //   - not manually dismissed
  const shouldOfferDiagnostic = !!data
    && data.status !== "closed"
    && !diagDismissed
    && !(data.messages || []).some(m => m.author === "tenant");

  if (loading) return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-black uppercase text-slate-400 text-xs">Loading…</div>;
  if (!data) return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-black uppercase text-slate-500">Invalid link</div>;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col" data-testid="tb-tenant-view">
      <header className="bg-[#0f172a] text-white px-4 py-3">
        <div className="max-w-md mx-auto flex items-center gap-2">
          <Shield weight="fill" size={22} className="text-cyan-400 shrink-0"/>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-400">
              {data.address} {data.unit && `· ${data.unit}`}
            </div>
            <div className="font-black text-lg leading-tight truncate" data-testid="tb-tenant-subject">
              {data.subject}
            </div>
          </div>
        </div>
      </header>

      {data.description && (
        <div className="max-w-md mx-auto w-full p-3 bg-slate-50 border-b border-slate-200">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Initial report</div>
          <div className="text-sm text-slate-700 italic mt-1">{data.description}</div>
        </div>
      )}

      {/* ── AI Self-Diagnostic Card ─────────────────────────────
          Auto-suggests safe DIY steps the tenant can try before we
          dispatch a tech. Only shows on OPEN threads where the tenant
          hasn't chatted yet (so we don't nag). Dismissable + re-openable
          via a small link below. */}
      {shouldOfferDiagnostic && !diagOpen && (
        <div className="max-w-md mx-auto w-full p-3" data-testid="tb-diag-teaser">
          <button
            onClick={() => loadDiagnostic()}
            data-testid="tb-diag-open-btn"
            className="w-full text-left rounded-xl border-2 border-cyan-500 bg-cyan-50 hover:bg-cyan-100 transition-colors p-3 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-cyan-500 text-white flex items-center justify-center flex-shrink-0">
                <Sparkle weight="fill" size={20}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-black uppercase tracking-widest text-cyan-700">
                  {diagLang === "es" ? "Prueba esto primero — 2 min" : "Try This First — 2 min"}
                </div>
                <div className="text-sm font-black text-slate-900 mt-0.5">
                  {diagLang === "es"
                    ? "Unas revisiones rápidas antes de enviar un técnico"
                    : "A few quick things to check before we send a tech"}
                </div>
                <div className="text-xs text-slate-600 mt-1 leading-snug">
                  {diagLang === "es"
                    ? <>A veces es solo un botón de reset o un breaker. Toca para pasos seguros con IA para <b>{data.subject}</b>.</>
                    : <>Sometimes it's just a reset button or a tripped breaker. Tap for AI-suggested safe steps for <b>{data.subject}</b>.</>}
                </div>
              </div>
            </div>
          </button>
          <div className="flex items-center justify-between mt-1.5">
            <button
              onClick={() => setDiagDismissed(true)}
              data-testid="tb-diag-dismiss-btn"
              className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700 px-2 py-1">
              {diagLang === "es" ? "No, gracias — enviar mensaje" : "No thanks — skip to messaging"}
            </button>
            <button
              onClick={() => setDiagLang(l => l === "es" ? "en" : "es")}
              data-testid="tb-diag-lang-toggle"
              title="Change language"
              className="text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:text-cyan-900 hover:underline px-2 py-1">
              🌐 {diagLang === "es" ? "English" : "Español"}
            </button>
          </div>
        </div>
      )}
      {shouldOfferDiagnostic && diagOpen && (
        <div className="max-w-md mx-auto w-full p-3" data-testid="tb-diag-panel">
          <div className="rounded-xl border-2 border-cyan-500 bg-white shadow-sm overflow-hidden">
            <div className="px-3 py-2 bg-cyan-500 text-white flex items-center gap-2">
              <Wrench weight="fill" size={16}/>
              <div className="flex-1 font-black text-xs uppercase tracking-widest">
                {diagLang === "es" ? "Reparaciones rápidas" : "Quick fixes to try"}
              </div>
              <button
                onClick={() => loadDiagnostic(diagLang === "es" ? "en" : "es")}
                data-testid="tb-diag-lang-toggle-open"
                title="Change language"
                className="h-6 px-1.5 rounded-md hover:bg-white/20 text-white flex items-center justify-center gap-1 text-[10px] font-black uppercase tracking-widest">
                🌐 {diagLang === "es" ? "EN" : "ES"}
              </button>
              <button
                onClick={() => setDiagOpen(false)}
                data-testid="tb-diag-close-btn"
                className="h-6 w-6 rounded-md hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="Close">
                <X weight="bold" size={14}/>
              </button>
            </div>
            <div className="p-3" data-testid="tb-diag-content">
              {diagBusy ? (
                <div className="text-xs text-slate-500 italic py-4 text-center">
                  {diagLang === "es"
                    ? "Preparando las revisiones más seguras para ti…"
                    : "Thinking through the safest checks for you…"}
                </div>
              ) : (
                <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                  {/* Tiny inline markdown scrubber — Claude sometimes wraps
                      key phrases in **bold**; the tenant portal isn't a
                      markdown renderer, so we visually promote them by
                      dropping the asterisks. */}
                  {(diagText || "").replace(/\*\*(.+?)\*\*/g, "$1")}
                </div>
              )}
            </div>
            {!diagBusy && diagText && (
              <>
                {diagVideo && (
                  <a
                    href={diagVideo}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="tb-diag-video-btn"
                    className="mx-3 mb-2 group relative block rounded-lg overflow-hidden border-2 border-red-800 shadow-sm hover:shadow-md transition-shadow">
                    {/* Preview thumbnail from YouTube's public CDN, cached
                        server-side for 7d/query. Falls back to a solid
                        red bar with the play icon if the fetch fails. */}
                    {diagThumb ? (
                      <div className="relative h-24 w-full bg-black">
                        <img src={diagThumb} alt="How-to video preview"
                          className="absolute inset-0 h-full w-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                          loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 via-red-900/30 to-transparent"/>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="h-11 w-11 rounded-full bg-red-600 group-hover:bg-red-500 text-white flex items-center justify-center shadow-lg">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                        <div className="absolute bottom-1.5 left-2 right-2 text-white font-black text-[10px] uppercase tracking-widest drop-shadow-md">
                          {diagLang === "es" ? "▶ Ver video de 60 seg" : "▶ Watch a 60-sec how-to"}
                        </div>
                      </div>
                    ) : (
                      <div className="h-11 bg-red-600 hover:bg-red-700 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                        {diagLang === "es" ? "Ver video de 60 seg" : "Watch a 60-sec how-to"}
                      </div>
                    )}
                  </a>
                )}
                <div className="px-3 pb-3 grid grid-cols-2 gap-2">
                  <button
                    onClick={markDiagnosticResolved}
                    data-testid="tb-diag-resolved-btn"
                    className="h-11 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-1.5 border-2 border-emerald-700 text-center">
                    <ThumbsUp weight="fill" size={14}/>
                    {diagLang === "es" ? "¡Se arregló!" : "That Fixed It!"}
                  </button>
                  <button
                    onClick={() => {
                      setDiagOpen(false);
                      setDiagDismissed(true);
                      setShowPhotoNudge(true);
                    }}
                    data-testid="tb-diag-dispatch-btn"
                    className="h-11 rounded-lg bg-[#0f172a] hover:bg-[#1e293b] text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-1.5 text-center px-2">
                    {diagLang === "es" ? "Sigue roto — enviar fotos" : "Still Broken — Send Photos"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Persistent "re-open quick fixes" link once tenant dismisses OR
          has already sent a message. Only if thread is still open. */}
      {data.status !== "closed" && (diagDismissed || (data.messages || []).some(m => m.author === "tenant")) && !diagOpen && (
        <div className="max-w-md mx-auto w-full px-3 -mt-1 mb-1">
          <button
            onClick={loadDiagnostic}
            data-testid="tb-diag-reopen-btn"
            className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-cyan-700 hover:text-cyan-900 hover:underline">
            <Sparkle weight="fill" size={12}/> Try AI Quick Fixes
          </button>
        </div>
      )}

      {/* ── Post-dispatch photo nudge ────────────────────────
          After tenant taps "Still Broken", we surface this card BEFORE
          the messenger. Encourages them to snap what they tried so the
          tech arrives already prepped. Composer stays visible below —
          they can still type — this just adds a strong visual cue and
          two big buttons that pre-attach photos via the existing pickers.
          Auto-dismisses once they attach anything or once they send. */}
      {data.status !== "closed" && showPhotoNudge && pending.length === 0 && (
        <div className="max-w-md mx-auto w-full p-3" data-testid="tb-photo-nudge">
          <div className="rounded-xl border-2 border-amber-500 bg-amber-50 p-3 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                <Camera weight="fill" size={20}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-black uppercase tracking-widest text-amber-800">
                  One quick step to help our tech
                </div>
                <div className="text-sm font-black text-slate-900 mt-0.5">
                  Snap a photo of what you tried
                </div>
                <div className="text-xs text-slate-700 mt-1 leading-snug">
                  Show the disposal, breaker, or outlet you checked — even if nothing changed. This helps our tech arrive with the right tools and a game plan.
                </div>
              </div>
              <button
                onClick={() => setShowPhotoNudge(false)}
                data-testid="tb-photo-nudge-dismiss"
                className="h-6 w-6 rounded-md hover:bg-amber-200 text-amber-900 flex items-center justify-center flex-shrink-0"
                aria-label="Skip">
                <X weight="bold" size={14}/>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                onClick={() => cameraInputRef.current?.click()}
                data-testid="tb-photo-nudge-camera-btn"
                className="h-11 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-black text-xs uppercase tracking-widest inline-flex items-center justify-center gap-1.5 border-2 border-amber-800">
                <Camera weight="fill" size={14}/> Take Photo
              </button>
              <button
                onClick={() => galleryInputRef.current?.click()}
                data-testid="tb-photo-nudge-gallery-btn"
                className="h-11 rounded-lg bg-white hover:bg-slate-50 text-slate-900 font-black text-xs uppercase tracking-widest inline-flex items-center justify-center gap-1.5 border-2 border-slate-900">
                <ImageIcon weight="fill" size={14}/> From Gallery
              </button>
            </div>
            <button
              onClick={() => setShowPhotoNudge(false)}
              data-testid="tb-photo-nudge-skip"
              className="mt-2 w-full text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-800">
              Skip — just message the office
            </button>
          </div>
        </div>
      )}

      {(data.entry_notices || []).map(n => (
        <EntryNoticeCard key={n.id} notice={n} token={token} onReload={load}/>
      ))}

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 max-w-md mx-auto w-full" data-testid="tb-tenant-messages">
        {(data.messages || []).length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-xs italic">
            No messages yet. Your property manager will follow up shortly.
          </div>
        ) : (
          <div className="space-y-2">
            {data.messages.map((m, i) => {
              const photoCount = (m.photos || []).length;
              return (
                <div key={i} className={`flex ${m.from === "tenant" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] p-2.5 text-sm border-2 ${
                    m.from === "tenant" ? "bg-cyan-100 border-cyan-500" : "bg-white border-[#0f172a]"
                  }`}>
                    <div className="text-[9px] font-black uppercase tracking-widest opacity-60 mb-0.5">
                      {m.from === "tenant" ? "You" : "Property manager"} · {new Date(m.at).toLocaleString()}
                    </div>
                    {m.text && <div className="whitespace-pre-wrap">{m.text}</div>}
                    {photoCount > 0 && (
                      <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest ${
                        m.from === "tenant" ? "text-cyan-800" : "text-slate-600"
                      }`} data-testid={`tb-msg-photo-badge-${i}`}>
                        <ImageIcon weight="fill" size={12}/> {photoCount} photo{photoCount > 1 ? "s" : ""} sent
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {data.closed ? (
        <div className="max-w-md mx-auto w-full p-4 bg-emerald-50 border-t-2 border-emerald-500 text-center" data-testid="tb-tenant-closed">
          <CheckCircle weight="fill" size={28} className="text-emerald-500 mx-auto"/>
          <div className="font-black uppercase tracking-widest text-xs text-emerald-800 mt-2">Closed</div>
          <div className="text-[11px] text-emerald-700 mt-1">
            Contact the office directly if something new comes up.
          </div>
        </div>
      ) : (
        <div className="max-w-md mx-auto w-full bg-white border-t-2 border-slate-200 sticky bottom-0">
          {/* Pending photo preview strip */}
          {pending.length > 0 && (
            <div className="px-2 pt-2 pb-1 border-b border-slate-100 flex gap-2 overflow-x-auto" data-testid="tb-tenant-photo-strip">
              {pending.map((p) => (
                <div key={p.id} className="relative shrink-0" data-testid={`tb-tenant-photo-thumb-${p.id}`}>
                  <img src={p.dataUrl} alt={p.name}
                    className="h-16 w-16 object-cover border-2 border-[#0f172a]"/>
                  <button onClick={() => removePending(p.id)} data-testid={`tb-tenant-photo-remove-${p.id}`}
                    className="absolute -top-1 -right-1 h-5 w-5 bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center rounded-full shadow"
                    aria-label="Remove photo">
                    <X weight="bold" size={12}/>
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] font-black uppercase tracking-widest text-center px-0.5">
                    {p.sizeMB}MB
                  </div>
                </div>
              ))}
              {pending.length < MAX_PHOTOS && (
                <div className="shrink-0 h-16 w-16 border-2 border-dashed border-slate-300 flex items-center justify-center text-[9px] font-black uppercase tracking-widest text-slate-400">
                  {pending.length}/{MAX_PHOTOS}
                </div>
              )}
            </div>
          )}

          {/* Hidden file inputs */}
          <input ref={galleryInputRef} type="file" accept="image/*" multiple hidden
            data-testid="tb-tenant-file-input"
            onChange={(e) => addFiles(e.target.files)}/>
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden
            data-testid="tb-tenant-camera-input"
            onChange={(e) => addFiles(e.target.files)}/>

          <div className="p-2 flex gap-2 items-end">
            <button onClick={() => galleryInputRef.current?.click()}
              disabled={pending.length >= MAX_PHOTOS}
              data-testid="tb-tenant-attach-btn"
              className="h-11 w-11 shrink-0 border-2 border-[#0f172a] bg-white hover:bg-slate-50 disabled:opacity-30 flex items-center justify-center"
              title="Attach photos" aria-label="Attach photos">
              <Paperclip weight="bold" size={18}/>
            </button>
            <button onClick={() => cameraInputRef.current?.click()}
              disabled={pending.length >= MAX_PHOTOS}
              data-testid="tb-tenant-camera-btn"
              className="h-11 w-11 shrink-0 border-2 border-[#0f172a] bg-white hover:bg-slate-50 disabled:opacity-30 flex items-center justify-center sm:hidden"
              title="Take photo" aria-label="Take photo">
              <Camera weight="fill" size={18}/>
            </button>
            <input value={reply} onChange={e => setReply(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
              data-testid="tb-tenant-reply-input"
              placeholder={
                pending.length ? "Add a note (optional)…"
                : showPhotoNudge ? "e.g., I hit the reset button but it still hums"
                : "Type a reply…"
              }
              className="flex-1 h-11 px-3 border-2 border-[#0f172a] text-sm min-w-0"/>
            <button onClick={send} disabled={busy || (!reply.trim() && pending.length === 0)}
              data-testid="tb-tenant-reply-send"
              className="h-11 px-4 bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-40 shrink-0 inline-flex items-center gap-1 font-black uppercase tracking-widest text-[10px]">
              <PaperPlaneTilt weight="fill" size={16}/>
              {busy ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      )}

      <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 text-center py-3">
        Powered by Field Radar · your contact info stays private
      </div>
    </div>
  );
}
